import { useMemo } from 'react';
import { useAuthStore } from '@/store/authStore';

export interface CmsPermissions {
  role: string;
  // Cases
  canReadCases: boolean;
  canCreateCases: boolean;
  canUpdateCases: boolean;
  canDeleteCases: boolean;
  // Alerts
  canReadAlerts: boolean;
  canCreateAlerts: boolean;
  canUpdateAlerts: boolean;
  canDeleteAlerts: boolean;
  canCloseAlerts: boolean;
  canAcknowledgeAlerts: boolean;
  canWorkOnAlerts: boolean; // true for every role holding any alert-mutation permission (org_admin, tier1/2/3, manager)
  // Alert Sources
  canCreateAlertSources: boolean;
  canReadAlertSources: boolean;
  canUpdateAlertSources: boolean;
  canDeleteAlertSources: boolean;
  // Asset Criticality
  canManageAssetCriticality: boolean; // manager/org_admin — read is available to all analyst tiers
  // IOCs
  canCreateIocs: boolean;
  canReadIocs: boolean;
  canUpdateIocs: boolean;
  canDeleteIocs: boolean;
  // Users
  canManageUsers: boolean; // invite, role changes
  // Audit
  canReadAudit: boolean;
  // Comments
  canCreateComments: boolean;
  canReadComments: boolean;
  canDeleteComments: boolean;
  // Tasks
  canCreateTasks: boolean;
  canReadTasks: boolean;
  canUpdateTasks: boolean;
  canDeleteTasks: boolean;
  // Plan / Subscription
  canRequestUpgrade: boolean; // org_admin can request plan upgrades
  canManageSubscriptionRequests: boolean; // platform_admin can list/approve/reject
  // ── Workflow Permissions (State Machine) ──────────────────────────────────
  canEscalateCases: boolean;
  canReviewCases: boolean;
  canAssignCases: boolean;
  canStartInvestigation: boolean;
  canTransitionResponse: boolean;
  canPendCase: boolean;
  canReopenCases: boolean;
  canCloseCases: boolean;
  // ── New Entity Permissions ────────────────────────────────────────────────
  canManageObservables: boolean;
  canManageEvidence: boolean;
  canManageMitre: boolean;
  canManageCooperation: boolean;
  // ── UI Visibility (sidebar + settings tabs) ──────────────────────────────
  // Settings tabs
  canViewPricingCalculator: boolean;   // hidden for manager and below
  canViewRolesPermissions: boolean;    // hidden for manager and below
  canViewActiveSubscriptions: boolean; // platform_admin only
  canViewManagePricing: boolean;       // platform_admin only
  // Sidebar nav items
  canViewCaseBoard: boolean;           // manager + org_admin only
  canViewAlertSources: boolean;        // hidden for manager and below
  canViewTeamManagement: boolean;      // manager + org_admin
  canViewAuditLog: boolean;            // hidden for tier3 and below
  // ── Data Access Guards ────────────────────────────────────────────────────
  canViewCaseSummary: boolean;         // GET /cases/summary — manager, org_admin only
  canViewEscalationKpis: boolean;      // GET /cases/escalation-kpis — manager, org_admin only
  canManageAvailability: boolean;      // availability endpoints — manager, org_admin only
  canViewSettingsAndPlan: boolean;     // Settings & Plan sidebar item — manager+ only
  canManageCaseTimers: boolean;        // Case Timers tab in Settings — manager, org_admin only
  canViewPersonalMetrics: boolean;     // My Metrics page — tier1/2/3
  canPromoteAlertToCase: boolean;      // Promote alert → new case — tier1/2/3
  canLinkAlertToCase: boolean;         // Link alert → existing case — tier1/2/3
  // ── CERT Cooperation Request Workflow ──────────────────────────────
  canRequestCertCooperation: boolean; // tier1/2/3 — submit CERT collaboration request
  canReviewCertRequests: boolean;     // manager — approve / reject requests
  canViewCertRequests: boolean;       // all case participants — view request history
}

const ROLE_PRIORITY = [
  'platform_admin',
  'org_admin',
  'manager',
  'tier3',
  'tier2',
  'tier1',
];

const DEFAULT_PERMISSIONS: CmsPermissions = {
  role: 'none',
  canReadCases: true,
  canCreateCases: false,
  canUpdateCases: false,
  canDeleteCases: false,
  canReadAlerts: true,
  canCreateAlerts: false,
  canUpdateAlerts: false,
  canDeleteAlerts: false,
  canCloseAlerts: false,
  canAcknowledgeAlerts: false,
  canWorkOnAlerts: false,
  canCreateAlertSources: false,
  canReadAlertSources: false,
  canUpdateAlertSources: false,
  canDeleteAlertSources: false,
  canManageAssetCriticality: false,
  canCreateIocs: false,
  canReadIocs: true,
  canUpdateIocs: false,
  canDeleteIocs: false,
  canManageUsers: false,
  canReadAudit: false,
  canCreateComments: false,
  canReadComments: true,
  canDeleteComments: false,
  canCreateTasks: false,
  canReadTasks: true,
  canUpdateTasks: false,
  canDeleteTasks: false,
  canRequestUpgrade: false,
  canManageSubscriptionRequests: false,
  // Workflow
  canEscalateCases: false,
  canReviewCases: false,
  canAssignCases: false,
  canStartInvestigation: false,
  canTransitionResponse: false,
  canPendCase: false,
  canReopenCases: false,
  canCloseCases: false,
  // New entities
  canManageObservables: false,
  canManageEvidence: false,
  canManageMitre: false,
  canManageCooperation: false,
  // UI Visibility — most restricted defaults (no built-in role)
  canViewCaseBoard: false,
  canViewPricingCalculator: false,
  canViewRolesPermissions: false,
  canViewActiveSubscriptions: false,
  canViewManagePricing: false,
  canViewAlertSources: false,
  canViewTeamManagement: false,
  canViewAuditLog: false,
  // Data access guards
  canViewCaseSummary: false,
  canViewEscalationKpis: false,
  canManageAvailability: false,
  // Tier1-specific flags
  canViewSettingsAndPlan: false,
  canManageCaseTimers: false,
  canViewPersonalMetrics: false,
  canPromoteAlertToCase: false,
  canLinkAlertToCase: false,
  canRequestCertCooperation: false,
  canReviewCertRequests: false,
  canViewCertRequests: false,
};

const PERMISSIONS_MATRIX: Record<string, Partial<CmsPermissions>> = {
  platform_admin: {
    // platform_admin has full administrative oversight over the CMS module.
    canReadCases: true,
    canCreateCases: true,
    canUpdateCases: true,
    canDeleteCases: true,
    canReadAlerts: true,
    canCreateAlerts: true,
    canUpdateAlerts: true,
    canDeleteAlerts: true,
    canCloseAlerts: true,
    canAcknowledgeAlerts: true,
    canWorkOnAlerts: true,
    canCreateAlertSources: true,
    canReadAlertSources: true,
    canUpdateAlertSources: true,
    canDeleteAlertSources: true,
    canManageAssetCriticality: true,
    canCreateIocs: true,
    canReadIocs: true,
    canUpdateIocs: true,
    canDeleteIocs: true,
    canManageUsers: true,
    canReadAudit: true,
    canCreateComments: true,
    canReadComments: true,
    canDeleteComments: true,
    canCreateTasks: true,
    canReadTasks: true,
    canUpdateTasks: true,
    canDeleteTasks: true,
    // platform_admin approves upgrades; it has no organization of its own to
    // upgrade, so the request button would only ever fail for it.
    canRequestUpgrade: false,
    canManageSubscriptionRequests: true,
    // Workflow
    canEscalateCases: true,
    canReviewCases: true,
    canAssignCases: true,
    canStartInvestigation: true,
    canTransitionResponse: true,
    canPendCase: true,
    canReopenCases: true,
    canCloseCases: true,
    canManageObservables: true,
    canManageEvidence: true,
    canManageMitre: true,
    canManageCooperation: true,
    // UI visibility
    canViewCaseBoard: true,
    canViewPricingCalculator: true,
    canViewRolesPermissions: true,
    canViewActiveSubscriptions: true,
    canViewManagePricing: true,
    canViewAlertSources: true,
    canViewTeamManagement: true,
    canViewAuditLog: true,
    canViewCaseSummary: true,
    canViewEscalationKpis: true,
    canManageAvailability: true,
    canViewSettingsAndPlan: true,
    canManageCaseTimers: true,
    canViewPersonalMetrics: true,
    canPromoteAlertToCase: true,
    canLinkAlertToCase: true,
    canRequestCertCooperation: true,
    canReviewCertRequests: true,
    canViewCertRequests: true,
  },
  org_admin: {
    // Every case/alert/task/comment flag below is now driven by the real
    // @RequirePermissions(...) grants in SYSTEM_ROLES: org_admin holds the
    // full cases/alerts/tasks/comments CRUD set (cases:create/update/delete,
    // alerts:create/update/acknowledge/close, tasks:*, comments:*), not just
    // the read-only "Manager oversight" set this file assumed before the
    // canonical permission migration made those decorators real.
    canCreateCases: true,
    canUpdateCases: true,
    canDeleteCases: true,
    canCreateAlerts: true,
    canUpdateAlerts: true,
    canDeleteAlerts: false,
    canCloseAlerts: true,
    canAcknowledgeAlerts: true,
    canWorkOnAlerts: true,
    canCreateAlertSources: true,
    canReadAlertSources: true,
    canUpdateAlertSources: true,
    canDeleteAlertSources: true,
    canManageAssetCriticality: true,
    canCreateIocs: false,
    canUpdateIocs: false,
    canDeleteIocs: false,
    canManageUsers: true,
    canReadAudit: true,
    canCreateComments: true,
    canDeleteComments: true,
    canCreateTasks: true,
    canUpdateTasks: true,
    canDeleteTasks: true,
    canRequestUpgrade: true,
    // platform:orgs-manage (confirm/reject/exchange-rate/subscriptions list)
    // is platform_admin-only — no role is ever seeded with a platform:*
    // permission, org_admin included.
    canManageSubscriptionRequests: false,
    // Workflow — org_admin holds cases:update (escalate/start-investigation/
    // transition-response/pending/resume) and roles:manage (review/assign/
    // reopen — the supervisory bucket also gating case board/dashboard/
    // escalation-kpis/task-templates/IR-stage config below).
    canEscalateCases: true,
    canReviewCases: true,
    canAssignCases: true,
    canStartInvestigation: true,
    canTransitionResponse: true,
    canPendCase: true,
    canReopenCases: true,
    canCloseCases: true,
    canManageObservables: true,
    canManageEvidence: true,
    canManageMitre: true,
    canManageCooperation: true,
    // UI visibility — org_admin cannot see platform-admin-only tabs
    canViewCaseBoard: true,
    canViewPricingCalculator: true,
    canViewRolesPermissions: true,
    canViewActiveSubscriptions: false, // platform_admin only
    canViewManagePricing: false,       // platform_admin only
    canViewAlertSources: true,
    canViewTeamManagement: true,
    canViewAuditLog: true,
    canViewCaseSummary: true,
    canViewEscalationKpis: true,
    canManageAvailability: true,
    canViewSettingsAndPlan: true,
    canManageCaseTimers: true,
    canViewPersonalMetrics: false,
    canPromoteAlertToCase: true,
    canLinkAlertToCase: true,
    canRequestCertCooperation: true,
    canReviewCertRequests: true,
    canViewCertRequests: true,     // org_admin can view for oversight
  },
  // manager is a peer of org_admin, not a scaled-down variant — identical
  // permission grant in SYSTEM_ROLES, so identical flags here.
  manager: {
    canCreateCases: true,
    canUpdateCases: true,
    canDeleteCases: true,
    canCreateAlerts: true,
    canUpdateAlerts: true,
    canDeleteAlerts: false,
    canCloseAlerts: true,
    canAcknowledgeAlerts: true,
    canWorkOnAlerts: true,
    canCreateAlertSources: true,
    canReadAlertSources: true,
    canUpdateAlertSources: true,
    canDeleteAlertSources: true,
    canManageAssetCriticality: true,
    canCreateIocs: false,
    canUpdateIocs: false,
    canDeleteIocs: false,
    canManageUsers: true,
    canReadAudit: true,
    canCreateComments: true,
    canDeleteComments: true,
    canCreateTasks: true,
    canUpdateTasks: true,
    canDeleteTasks: true,
    canRequestUpgrade: true,
    canManageSubscriptionRequests: false,
    canEscalateCases: true,
    canReviewCases: true,
    canAssignCases: true,
    canStartInvestigation: true,
    canTransitionResponse: true,
    canPendCase: true,
    canReopenCases: true,
    canCloseCases: true,
    canManageObservables: true,
    canManageEvidence: true,
    canManageMitre: true,
    canManageCooperation: true,
    canViewCaseBoard: true,
    canViewPricingCalculator: true,
    canViewRolesPermissions: true,
    canViewActiveSubscriptions: false, // platform_admin only
    canViewManagePricing: false,       // platform_admin only
    canViewAlertSources: true,
    canViewTeamManagement: true,
    canViewAuditLog: true,
    canViewCaseSummary: true,
    canViewEscalationKpis: true,
    canManageAvailability: true,
    canViewSettingsAndPlan: true,
    canManageCaseTimers: true,
    canViewPersonalMetrics: false,
    canPromoteAlertToCase: true,
    canLinkAlertToCase: true,
    canRequestCertCooperation: true,
    canReviewCertRequests: true,
    canViewCertRequests: true,
  },
  tier3: {
    canCreateCases: true,
    canUpdateCases: true,
    canCreateAlerts: true,
    canUpdateAlerts: true,
    canAcknowledgeAlerts: true,
    canCloseAlerts: true,            // tier2/tier3/manager/org_admin hold alerts:close — tier1 doesn't
    canWorkOnAlerts: true,
    canReadAlertSources: true,
    canCreateAlertSources: false,
    canUpdateAlertSources: false,
    canDeleteAlertSources: false,
    canCreateIocs: true,
    canUpdateIocs: true,
    canDeleteIocs: true,
    canCreateComments: true,
    canDeleteComments: true,
    canCreateTasks: true,
    canUpdateTasks: true,
    // Workflow — tier3 is senior investigator: can escalate, start, transition, pend
    canEscalateCases: true,    // POST :id/escalate is @Roles('tier1','tier2','tier3')
    canReviewCases: false,
    canAssignCases: false,
    canStartInvestigation: true,
    canTransitionResponse: true,
    canPendCase: true,
    canReopenCases: false,
    canCloseCases: false,
    canManageObservables: true,
    canManageEvidence: true,
    canManageMitre: true,
    canManageCooperation: false,
    // UI visibility — tier3 same as manager but also hides audit log
    canViewPricingCalculator: false,
    canViewRolesPermissions: false,
    canViewActiveSubscriptions: false,
    canViewManagePricing: false,
    canViewAlertSources: true,
    canViewTeamManagement: false,
    canViewAuditLog: false,
    canViewCaseSummary: true,
    canViewSettingsAndPlan: false,
    canViewPersonalMetrics: true,
    canPromoteAlertToCase: true,      // cases:create — held by every analyst tier plus manager/org_admin
    canLinkAlertToCase: true,         // tier2/tier3/manager/org_admin hold alerts:link — tier1 doesn't
    canRequestCertCooperation: true,  // tier3 can request CERT collaboration
    canReviewCertRequests: false,
    canViewCertRequests: true,
  },

  tier2: {
    canCreateCases: true,
    canUpdateCases: true,
    canCreateAlerts: true,
    canUpdateAlerts: true,
    canAcknowledgeAlerts: true,
    canCloseAlerts: true,            // tier2/tier3/manager/org_admin hold alerts:close — tier1 doesn't
    canWorkOnAlerts: true,
    canReadAlertSources: true,
    canCreateAlertSources: false,
    canUpdateAlertSources: false,
    canDeleteAlertSources: false,
    canCreateIocs: true,
    canUpdateIocs: true,
    canCreateComments: true,
    canCreateTasks: true,
    canUpdateTasks: true,
    // Workflow — tier2 is standard investigator: can escalate, start, transition, pend
    canEscalateCases: true,    // POST :id/escalate is @Roles('tier1','tier2','tier3')
    canReviewCases: false,
    canAssignCases: false,
    canStartInvestigation: true,
    canTransitionResponse: true,
    canPendCase: true,
    canReopenCases: false,
    canCloseCases: false,
    canManageObservables: true,
    canManageEvidence: true,
    canManageMitre: true,
    canManageCooperation: false,
    // UI visibility — same restrictions as tier3
    canViewPricingCalculator: false,
    canViewRolesPermissions: false,
    canViewActiveSubscriptions: false,
    canViewManagePricing: false,
    canViewAlertSources: true,
    canViewTeamManagement: false,
    canViewAuditLog: false,
    canViewCaseSummary: true,
    canViewSettingsAndPlan: false,
    canViewPersonalMetrics: true,
    canPromoteAlertToCase: true,      // cases:create — held by every analyst tier plus manager/org_admin
    canLinkAlertToCase: true,         // tier2/tier3/manager/org_admin hold alerts:link — tier1 doesn't
    canRequestCertCooperation: true,  // tier2 can request CERT collaboration
    canReviewCertRequests: false,
    canViewCertRequests: true,
  },

  tier1: {
    canCreateCases: true,
    canAcknowledgeAlerts: true,
    canCloseAlerts: true,           // close as false positive (reason required in UI) — SYSTEM_ROLES tier1 now includes alerts:close
    canWorkOnAlerts: true,          // full action rights on alerts (triage, tag, add/remove observables/mitre/attachments)
    canUpdateAlerts: true,
    canReadAlertSources: true,
    canCreateAlertSources: false,
    canUpdateAlertSources: false,
    canDeleteAlertSources: false,
    canCreateComments: true,
    canUpdateTasks: true,
    canCreateTasks: false,
    // Workflow — tier1 is triage analyst: can escalate only
    canEscalateCases: true,
    canReviewCases: false,
    canAssignCases: false,
    canStartInvestigation: false,
    canTransitionResponse: false,
    canPendCase: false,
    canReopenCases: false,
    canCloseCases: false,
    canManageObservables: true,     // add observables to own case (backend ownership enforced)
    canManageEvidence: true,        // register evidence on own case (backend ownership enforced)
    canManageMitre: true,           // per-case MITRE mapping is cases:update, which tier1 holds
    canManageCooperation: false,
    // UI visibility
    canViewPricingCalculator: false,
    canViewRolesPermissions: false,
    canViewActiveSubscriptions: false,
    canViewManagePricing: false,
    canViewAlertSources: true,
    canViewTeamManagement: false,
    canViewAuditLog: false,
    canViewCaseSummary: true,
    canViewSettingsAndPlan: false,
    canViewPersonalMetrics: true,
    canPromoteAlertToCase: true,
    canLinkAlertToCase: false,        // alerts:link is not in tier1's permission set (tier2/3/manager/org_admin only)
    canRequestCertCooperation: true,  // cases:update — tier1 holds it; CERT request submission is no longer tier2/3-exclusive
    canReviewCertRequests: false,
    canViewCertRequests: true,
  },
};

type CmsFlag = Exclude<keyof CmsPermissions, 'role'>;

/**
 * Flags whose endpoint is gated by these permissions alone, so the flag
 * follows the user's resolved permissions exactly: ticking or unticking the
 * permission in Roles & Permissions shows or hides the action. For every
 * built-in role with its default grant this yields the same value as
 * PERMISSIONS_MATRIX (pinned by test/cmsPermissions.catalog.test.ts).
 */
const PERMISSION_FLAGS: Partial<Record<CmsFlag, string[]>> = {
  canCreateCases: ['cases:create'],
  canUpdateCases: ['cases:update'],
  canEscalateCases: ['cases:update'],
  canManageObservables: ['cases:update'],
  canManageEvidence: ['cases:update'],
  canManageMitre: ['cases:update'],
  canRequestCertCooperation: ['cases:update'],
  canViewCertRequests: ['cases:read'],
  canPromoteAlertToCase: ['cases:create'],
  canLinkAlertToCase: ['alerts:link'],
  canWorkOnAlerts: ['alerts:update'],
  canAcknowledgeAlerts: ['alerts:acknowledge'],
  canCloseAlerts: ['alerts:close'],
  canViewAlertSources: ['alert-sources:read'],
  canCreateAlertSources: ['alert-sources:create'],
  canDeleteAlertSources: ['alert-sources:delete'],
  canCreateComments: ['comments:create'],
  canCreateTasks: ['tasks:create'],
  canReadAudit: ['audit:read'],
  canViewAuditLog: ['audit:read'],
  canManageUsers: ['users:invite'],
  canViewTeamManagement: ['users:read'],
  canManageAvailability: ['users:update'],
  canManageCaseTimers: ['roles:manage'],
};

/**
 * Flags whose action the service ALSO gates on the role name (isSocLeader for
 * review/assign/reopen/close and the case board, the tier1 triage rules,
 * own-or-admin comment deletes). These keep their per-role value from
 * PERMISSIONS_MATRIX and turn off when the permission is removed, but granting
 * the permission alone does not turn them on: the server would refuse it.
 */
const ROLE_GATED_FLAGS: Partial<Record<CmsFlag, string[]>> = {
  canReviewCases: ['roles:manage'],
  canAssignCases: ['roles:manage'],
  canReopenCases: ['roles:manage'],
  canCloseCases: ['cases:update'],
  canViewCaseBoard: ['roles:manage'],
  canStartInvestigation: ['cases:update'],
  canTransitionResponse: ['cases:update'],
  canPendCase: ['cases:update'],
  canDeleteComments: ['comments:delete'],
  canManageCooperation: ['roles:manage'],
  canReviewCertRequests: ['roles:manage'],
};

/** Actions a role is never offered even though it holds the permission. */
const HIDDEN_FOR_ROLE: Partial<Record<string, CmsFlag[]>> = {
  // tier1 is the triage analyst: it holds cases:update and tasks:create, but only
  // edits the case it escalated itself (ownership, see CaseDetailsPage), and task
  // creation is left to tier2+.
  tier1: ['canUpdateCases', 'canCreateTasks'],
};

/** A role's flags from PERMISSIONS_MATRIX alone, before permissions are applied. */
export function cmsRoleDefaults(role: string): CmsPermissions {
  return { ...DEFAULT_PERMISSIONS, ...(PERMISSIONS_MATRIX[role] ?? {}), role };
}

/**
 * The CMS flags for a user, from their realm roles and the permissions the
 * server resolved for them (GET /users/me, or /cms-users/me in the CMS
 * bundle). Every other flag is a product decision about what each role sees
 * and stays as PERMISSIONS_MATRIX defines it.
 */
export function resolveCmsPermissions(roles: string[], granted: string[]): CmsPermissions {
  // No built-in role (a custom role, or none at all): DEFAULT_PERMISSIONS, so
  // only the permission-backed flags below can turn anything on.
  const activeRole = ROLE_PRIORITY.find((role) => roles.includes(role)) ?? 'none';
  const flags = cmsRoleDefaults(activeRole);

  const holds = (needed: string[]) =>
    granted.includes('*') || needed.every((permission) => granted.includes(permission));
  const hidden = HIDDEN_FOR_ROLE[activeRole] ?? [];

  for (const [flag, needed] of Object.entries(PERMISSION_FLAGS) as [CmsFlag, string[]][]) {
    flags[flag] = holds(needed) && !hidden.includes(flag);
  }
  for (const [flag, needed] of Object.entries(ROLE_GATED_FLAGS) as [CmsFlag, string[]][]) {
    flags[flag] = flags[flag] && holds(needed);
  }
  return flags;
}

export function useCmsPermissions(): CmsPermissions {
  const user = useAuthStore((state) => state.user);
  const granted = useAuthStore((state) => state.permissions);

  return useMemo(
    () => resolveCmsPermissions(user?.roles ?? [], granted ?? []),
    [user, granted],
  );
}
